import * as F from "fp-ts/function"
import { useEffect } from "react"
import * as O from "fp-ts/Option"
import { SuiteNumber } from "@ssc-booking/api-contracts"
import * as RONEA from "fp-ts/ReadonlyNonEmptyArray"
import { useECommerceMachine, useWorkQueueMachine } from "../../../../_common/machines/machines-context"
import { SuiteWithPriceAndConfiguration } from "../../read-models/suite-category"
import { SanitySuite } from "../../../../_common/sanity/sanity-types"
import { useAvailableSuitesLoader } from "../ecommerce-machine/data-loading/available-suites-loader-machine"
import { maybeSelectedSuiteCategory, useSuiteCategoriesReadModel } from "../suite-categories-machine"
import { delay } from "../../../../_common/promise"
import { FareCard } from "../../read-models/fare-card"

export const useAutomaticFareSelectionEcommerceV4 = () => {
    const [state, send] = useECommerceMachine()
    const [stateWQ] = useWorkQueueMachine()

    const isSelectable = state.matches("flow.suiteCategorySelectedV4")
    const isAlreadySelected = state.context.selectedFare !== undefined
    const isWorkInProgress = !stateWQ.matches("empty")

    const cheapest = FareCard.cheapest(state.context.fareCards)

    useEffect(() => {
        if (isWorkInProgress || !isSelectable || isAlreadySelected || O.isNone(cheapest)) {
            return
        }

        delay(300).then(() =>
            send({
                type: "SELECT_FARE",
                key: cheapest.value.key,
                isAutomaticSelection: true,
            }),
        )
    }, [isWorkInProgress, isSelectable, isAlreadySelected, cheapest, send])
}

export const useAutomaticSuiteCategorySelectionEcommerceV4 = () => {
    const [state, send] = useECommerceMachine()
    const [stateWQ] = useWorkQueueMachine()

    const suiteCategorySelectable = state.matches("flow.nothingSelectedV4.suiteCategorySelectable")
    const alreadySelectedSuiteCategory = O.fromNullable(state.context.selectedSuiteCategory)
    const isWorkInProgress = !stateWQ.matches("empty")

    const lowestSuiteCategory = FareCard.cheapest(state.context.fareCards)

    useEffect(() => {
        if (
            isWorkInProgress ||
            !suiteCategorySelectable ||
            O.isSome(alreadySelectedSuiteCategory) ||
            O.isNone(lowestSuiteCategory)
        ) {
            return
        }

        delay(300).then(() =>
            send({
                type: "SELECT_SUITE_CATEGORY",
                suiteCategory: lowestSuiteCategory.value.response.suiteCategory,
                isAutomaticSelection: true,
            }),
        )
    }, [isWorkInProgress, suiteCategorySelectable, alreadySelectedSuiteCategory, lowestSuiteCategory, send])
}

const isGuaranteedSuite = (category: O.Option<SuiteWithPriceAndConfiguration<SanitySuite>>) => {
    return F.pipe(
        category,
        O.map((x) => x.price.availability === "guaranteed"),
        O.getOrElse(() => false),
    )
}
const isStandardSuite = (category: O.Option<SuiteWithPriceAndConfiguration<SanitySuite>>) => {
    return F.pipe(
        category,
        O.map((x) => x.price.availability === "standard"),
        O.getOrElse(() => false),
    )
}

export const useAutomaticStandardSuiteNumberSelectionEcommerceV4 = () => {
    const [state, send] = useECommerceMachine()
    const [stateAS] = useAvailableSuitesLoader(state)
    const [stateSCRM] = useSuiteCategoriesReadModel(state)
    const [stateWQ] = useWorkQueueMachine()

    const suiteNumberSelectable = state.matches("flow.fareSelectedV4.suiteNumberSelectable")
    const selectedSuiteCategory = maybeSelectedSuiteCategory(stateSCRM)
    const isStandard = isStandardSuite(selectedSuiteCategory)
    const alreadySelectedSuiteNumber = O.fromNullable(state.context.selectedSuiteNumber)
    const isWorkInProgress = !stateWQ.matches("empty")

    const firstSuiteNumber = F.pipe(
        RONEA.fromReadonlyArray(stateAS.context.response?.suites || []),
        O.map((availableSuites) => availableSuites[0].number),
    )

    useEffect(() => {
        if (
            isWorkInProgress ||
            !suiteNumberSelectable ||
            !isStandard ||
            O.isSome(alreadySelectedSuiteNumber) ||
            O.isNone(firstSuiteNumber)
        ) {
            return
        }

        send({ type: "SELECT_SUITE_NUMBER", suiteNumber: firstSuiteNumber.value })
    }, [isWorkInProgress, suiteNumberSelectable, isStandard, alreadySelectedSuiteNumber, firstSuiteNumber, send])
}

export const useAutomaticGuaranteedSuiteNumberSelectionEcommerceV4 = () => {
    const [state, send] = useECommerceMachine()
    const [stateSCRM] = useSuiteCategoriesReadModel(state)
    const [stateWQ] = useWorkQueueMachine()

    const selectedSuiteCategory = maybeSelectedSuiteCategory(stateSCRM)
    const suiteNumberSelectable = state.matches("flow.fareSelectedV4.suiteNumberSelectable")
    const isGuaranteed = isGuaranteedSuite(selectedSuiteCategory)
    const alreadySelectedSuiteNumber = O.fromNullable(state.context.selectedSuiteNumber)
    const isWorkInProgress = !stateWQ.matches("empty")

    useEffect(() => {
        if (isWorkInProgress || !suiteNumberSelectable || !isGuaranteed || O.isSome(alreadySelectedSuiteNumber)) {
            return
        }

        send({ type: "SELECT_SUITE_NUMBER", suiteNumber: SuiteNumber.guaranteed })
    }, [isWorkInProgress, suiteNumberSelectable, isGuaranteed, alreadySelectedSuiteNumber, send])
}

export const useAutomaticSuiteNumberSelectionEcommerceV4 = () => {
    useAutomaticStandardSuiteNumberSelectionEcommerceV4()
    useAutomaticGuaranteedSuiteNumberSelectionEcommerceV4()
}
